import { generateUniqueId } from '../../common/utils/common';
import { isChildrenProp, isEmptyString } from '../../common/utils/block';
import { sanitizeHeadingProps } from '../../common/utils/transform';
import * as constants from '../../common/constant';
import { BlockType, ContentType } from '../../models/enums';
/**
 * Factory class for creating block models and content
 */
export class BlockFactory {
    static createBlockFromPartial(block) {
        switch (block.blockType) {
            case BlockType.Paragraph:
                return this.createParagraphBlock(block, block.properties);
            case BlockType.Heading:
                return this.createHeadingBlock(block, block.properties);
            case BlockType.Checklist:
                return this.createChecklistBlock(block, block.properties);
            case BlockType.BulletList:
                return this.createBulletListBlock(block, block.properties);
            case BlockType.NumberedList:
                return this.createNumberedListBlock(block, block.properties);
            case BlockType.Code:
                return this.createCodeBlock(block, block.properties);
            case BlockType.Quote:
                return this.createQuoteBlock(block, block.properties);
            case BlockType.Callout:
                return this.createCalloutBlock(block, block.properties);
            case BlockType.Divider:
                return this.createDividerBlock(block, block.properties);
            case BlockType.CollapsibleParagraph:
                return this.createCollapsibleParagraphBlock(block, block.properties);
            case BlockType.CollapsibleHeading:
                return this.createCollapsibleHeadingBlock(block, block.properties);
            case BlockType.Image:
                return this.createImageBlock(block, block.properties);
            case BlockType.Table:
                return this.createTableBlock(block, block.properties);
            case BlockType.Template:
                return this.createTemplateBlock(block, block.properties);
            default:
                return null;
        }
    }
    static createContentFromPartial(content) {
        switch (content.contentType) {
            case ContentType.Text:
                return this.createTextContent(content, content.properties);
            case ContentType.Link:
                return this.createLinkContent(content, content.properties);
            case ContentType.Mention:
                return this.createMentionContent(content, content.properties);
            case ContentType.Label:
                return this.createLabelContent(content, content.properties);
            default:
                return null;
        }
    }
    /**
     * Creates a checklist block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block model
     * @param {Partial<IChecklistBlockSettings>} innerProps Optional props for the checklist
     * @returns {BlockModel} A new checklist block
     */
    static createChecklistBlock(rootProps = {}, innerProps = {}) {
        return Object.assign({ blockType: BlockType.Checklist }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { properties: Object.assign({ isChecked: false }, this.defaultInnerBlockProps, innerProps) });
    }
    /**
     * Creates a paragraph block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block model
     * @param {Partial<IParagraphBlockSettings>} innerProps Optional props for the paragraph
     * @returns {BlockModel} A new paragraph block
     */
    static createParagraphBlock(rootProps = {}, innerProps = {}) {
        return Object.assign({ blockType: BlockType.Paragraph }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { properties: Object.assign({}, this.defaultInnerBlockProps, innerProps) });
    }
    /**
     * Creates a heading block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<IHeadingBlockSettings>} innerProps Optional content for the heading
     * @returns {BlockModel} new heading block
     */
    static createHeadingBlock(rootProps = {}, innerProps = {}) {
        const sanitizedInnerProps = sanitizeHeadingProps(innerProps);
        return Object.assign({ blockType: BlockType.Heading }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { properties: Object.assign({ level: 1 }, this.defaultInnerBlockProps, sanitizedInnerProps) });
    }
    /**
     * Creates an image block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<IImageBlockSettings>} innerProps Optional props for the image
     * @returns {BlockModel} A new image block
     */
    static createImageBlock(rootProps = {}, innerProps = {}) {
        return Object.assign({ blockType: BlockType.Image }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { content: [], properties: Object.assign({ src: '', altText: '', width: '', height: '' }, innerProps) });
    }
    /**
     * Creates a code block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<ICodeBlockSettings>} innerProps Optional props for the code
     * @returns {BlockModel} A new code block
     */
    static createCodeBlock(rootProps = {}, innerProps = {}) {
        return Object.assign({ blockType: BlockType.Code }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { properties: Object.assign({ language: 'plaintext' }, innerProps) });
    }
    /**
     * Creates a bullet list block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<IBulletListBlockSettings>} innerProps Optional props for the bullet list
     * @returns {BlockModel} A new bullet list block
     */
    static createBulletListBlock(rootProps = {}, innerProps = {}) {
        return Object.assign({ blockType: BlockType.BulletList }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { properties: Object.assign({}, this.defaultInnerBlockProps, innerProps) });
    }
    /**
     * Creates a numbered list block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<INumberedListBlockSettings>} innerProps Optional props for the numbered list
     * @returns {BlockModel} A new bullet list block
     */
    static createNumberedListBlock(rootProps = {}, innerProps = {}) {
        return Object.assign({ blockType: BlockType.NumberedList }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { properties: Object.assign({}, this.defaultInnerBlockProps, innerProps) });
    }
    /**
     * Creates a quote block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<IQuoteBlockSettings>} innerProps Optional props for the quote
     * @returns {BlockModel} A new quote block
     */
    static createQuoteBlock(rootProps = {}, innerProps = {}) {
        const blockId = isEmptyString(rootProps.id) ? generateUniqueId(constants.BLOCK_ID_PREFIX) : rootProps.id;
        return Object.assign({ blockType: BlockType.Quote }, this.defaultRootBlockProps, rootProps, { id: blockId, content: [], properties: Object.assign({ children: [
                    BlockFactory.createParagraphBlock({
                        parentId: blockId,
                        content: [BlockFactory.createTextContent()]
                    })
                ] }, innerProps) });
    }
    /**
     * Creates a Collapsible paragraph block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<ICollapsibleBlockSettings>} innerProps Optional props for the Collapsible paragraph
     * @returns {BlockModel} A new Collapsible paragraph block
     */
    static createCollapsibleParagraphBlock(rootProps = {}, innerProps = {}) {
        const blockId = isEmptyString(rootProps.id) ? generateUniqueId(constants.BLOCK_ID_PREFIX) : rootProps.id;
        return Object.assign({ blockType: BlockType.CollapsibleParagraph }, this.defaultRootBlockProps, rootProps, { id: blockId, properties: Object.assign({ isExpanded: false, children: [
                    BlockFactory.createParagraphBlock({
                        parentId: blockId,
                        content: [BlockFactory.createTextContent()]
                    })
                ] }, this.defaultInnerBlockProps, innerProps) });
    }
    /**
     * Creates a collapsible heading block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<ICollapsibleHeadingBlockSettings>} innerProps Optional props for the collapsible heading
     * @returns {BlockModel} A new collapsible heading block
     */
    static createCollapsibleHeadingBlock(rootProps = {}, innerProps = {}) {
        const sanitizedInnerProps = sanitizeHeadingProps(innerProps);
        const blockId = isEmptyString(rootProps.id) ? generateUniqueId(constants.BLOCK_ID_PREFIX) : rootProps.id;
        return Object.assign({ blockType: BlockType.CollapsibleHeading }, this.defaultRootBlockProps, rootProps, { id: blockId, properties: Object.assign({ isExpanded: false, level: 1, children: [
                    BlockFactory.createParagraphBlock({
                        parentId: blockId,
                        content: [BlockFactory.createTextContent()]
                    })
                ] }, this.defaultInnerBlockProps, sanitizedInnerProps) });
    }
    /**
     * Creates a callout block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<ICalloutBlockSettings>} innerProps Optional props for the callout
     * @returns {BlockModel} A new callout block
     */
    static createCalloutBlock(rootProps = {}, innerProps = {}) {
        const blockId = isEmptyString(rootProps.id) ? generateUniqueId(constants.BLOCK_ID_PREFIX) : rootProps.id;
        return Object.assign({ blockType: BlockType.Callout }, this.defaultRootBlockProps, rootProps, { id: blockId, content: [], properties: Object.assign({ children: [
                    BlockFactory.createParagraphBlock({
                        parentId: blockId,
                        content: [BlockFactory.createTextContent()]
                    })
                ] }, innerProps) });
    }
    /**
     * Creates a divider block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<IDividerBlockSettings>} innerProps Optional props for the divider
     * @returns {BlockModel} A new divider block
     */
    static createDividerBlock(rootProps = {}, innerProps = {}) {
        return Object.assign({ blockType: BlockType.Divider }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { content: [], properties: Object.assign({}, innerProps) });
    }
    /**
     * Creates a template block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block model
     * @param {any} innerProps Optional props for the template
     * @returns {BlockModel} A new template block
     */
    static createTemplateBlock(rootProps = {}, innerProps = {}) {
        return Object.assign({ blockType: 'Template' }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { content: [], properties: Object.assign({}, innerProps) });
    }
    /**
     * Creates an table block
     *
     * @param {Partial<BlockModel>} rootProps Optional props for the block
     * @param {Partial<ITableBlockSettings>} innerProps Optional props for the image
     * @returns {BlockModel} A new image block
     */
    static createTableBlock(rootProps = {}, innerProps = {}) {
        // ParentIds for inner blocks will be assigned by the caller/state manager as needed
        const tableBlock = Object.assign({ blockType: BlockType.Table }, this.defaultRootBlockProps, rootProps, (isEmptyString(rootProps.id) ? { id: generateUniqueId(constants.BLOCK_ID_PREFIX) } : {}), { properties: Object.assign({ width: '100%', cssClass: '', enableHeader: true, enableRowNumbers: true, readOnly: false }, this.getDefaultRowsAndColumns(), innerProps) });
        tableBlock.properties.columns.forEach((column, idx) => {
            if (!column.id) {
                column.id = generateUniqueId('col_');
            }
            if (!column.type) {
                column.type = 'Text';
            }
            if (!column.headerText) {
                column.headerText = `Column ${idx + 1}`;
            }
            if (column.width) {
                let value = parseInt(column.width.toString(), 10);
                value = value < constants.TABLE_COL_MIN_WIDTH ? constants.TABLE_COL_MIN_WIDTH : value;
            }
        });
        tableBlock.properties.rows.forEach((r) => {
            if (!r.id) {
                r.id = generateUniqueId('row_');
            }
            r.cells.forEach((cell, idx) => {
                if (!cell.id) {
                    cell.id = generateUniqueId('cell_');
                }
                if (!cell.columnId) {
                    cell.columnId = tableBlock.properties.columns[idx].id;
                }
                cell.blocks = this.populateBlockProperties(cell.blocks, cell.id);
            });
        });
        return tableBlock;
    }
    static getDefaultRowsAndColumns() {
        // Defaults for new table structure: 2 columns, 2 rows (data), header enabled
        const col1Id = generateUniqueId('col_');
        const col2Id = generateUniqueId('col_');
        const row1Id = generateUniqueId('row_');
        const row2Id = generateUniqueId('row_');
        const cell11Id = generateUniqueId('cell_');
        const cell12Id = generateUniqueId('cell_');
        const cell21Id = generateUniqueId('cell_');
        const cell22Id = generateUniqueId('cell_');
        return {
            columns: [
                { id: col1Id, type: 'Text', headerText: 'Column 1' },
                { id: col2Id, type: 'Text', headerText: 'Column 2' }
            ],
            rows: [
                {
                    id: row1Id,
                    cells: [
                        { id: cell11Id, columnId: col1Id, blocks: [this.createParagraphBlock({ content: [this.createTextContent()] })] },
                        { id: cell12Id, columnId: col2Id, blocks: [this.createParagraphBlock({ content: [this.createTextContent()] })] }
                    ]
                },
                {
                    id: row2Id,
                    cells: [
                        { id: cell21Id, columnId: col1Id, blocks: [this.createParagraphBlock({ content: [this.createTextContent()] })] },
                        { id: cell22Id, columnId: col2Id, blocks: [this.createParagraphBlock({ content: [this.createTextContent()] })] }
                    ]
                }
            ]
        };
    }
    /**
     * Populates blocks with missing properties if they don't have them
     *
     * @param {BlockModel[]} blocks Array of block models
     * @param {string} parentId The id of the parent block
     * @returns {BlockModel[]} Updated array of block models
     */
    static populateBlockProperties(blocks, parentId) {
        const populatedBlocks = blocks.map((block) => {
            if (parentId) {
                block.parentId = parentId;
            }
            const updatedBlock = BlockFactory.createBlockFromPartial(block);
            if (updatedBlock.content && updatedBlock.content.length > 0) {
                updatedBlock.content = updatedBlock.content.map((originalContent) => {
                    return BlockFactory.createContentFromPartial(originalContent);
                });
            }
            const props = updatedBlock.properties;
            if ((isChildrenProp(updatedBlock)) && props.children.length > 0) {
                props.children = this.populateBlockProperties(props.children, block.id);
            }
            return updatedBlock;
        });
        return populatedBlocks;
    }
    /**
     * Creates a text content
     *
     * @param {Partial<ContentModel>} rootProps Optional props for the content
     * @param {Partial<ITextContentSettings>} innerProps Optional props for the text content
     * @returns {ContentModel} A new text content
     */
    static createTextContent(rootProps = {}, innerProps = {}) {
        return Object.assign({ contentType: ContentType.Text }, this.defaultRootContentProps, rootProps, { properties: Object.assign({ styles: {} }, innerProps) });
    }
    /**
     * Creates a link content
     *
     * @param {Partial<ContentModel>} rootProps Optional props for the content
     * @param {Partial<ILinkContentSettings>} innerProps Optional props for the link content
     * @returns {ContentModel} A new link content
     */
    static createLinkContent(rootProps = {}, innerProps = {}) {
        return Object.assign({ contentType: ContentType.Link }, this.defaultRootContentProps, rootProps, { properties: Object.assign({ styles: {}, url: '' }, innerProps) });
    }
    /**
     * Creates a mention content
     *
     * @param {Partial<ContentModel>} rootProps Optional props for the content
     * @param {Partial<IMentionContentSettings>} innerProps Optional props for the mention content
     * @returns {ContentModel} A new mention content
     */
    static createMentionContent(rootProps = {}, innerProps = {}) {
        return Object.assign({ contentType: ContentType.Mention }, this.defaultRootContentProps, rootProps, { properties: Object.assign({ userId: '' }, innerProps) });
    }
    /**
     * Creates a label content
     *
     * @param {Partial<ContentModel>} rootProps Optional props for the content
     * @param {Partial<ILabelContentSettings>} innerProps Optional props for the label content
     * @returns {ContentModel} A new label content
     */
    static createLabelContent(rootProps = {}, innerProps = {}) {
        return Object.assign({ contentType: ContentType.Label }, this.defaultRootContentProps, rootProps, { properties: Object.assign({ labelId: '' }, innerProps) });
    }
}
BlockFactory.defaultInnerBlockProps = {
    placeholder: ''
};
BlockFactory.defaultRootContentProps = {
    content: ''
};
BlockFactory.defaultRootBlockProps = {
    parentId: '',
    indent: 0,
    content: [BlockFactory.createTextContent()],
    cssClass: '',
    template: ''
};
